/**
 * Reusable Hero Component that works with the funnel configuration system
 */

import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MediaPresentation } from './MediaPresentation';
import { FunnelQuiz } from './FunnelQuiz';
import { useFunnelConfig } from '../hooks/use-funnel-config';

interface FunnelHeroProps {
  onStartQuiz: () => void;
  quizStarted: boolean;
  onQuizComplete: (formData: any, contactId?: string) => void;
}

export function FunnelHero({
  onStartQuiz,
  quizStarted,
  onQuizComplete,
}: FunnelHeroProps) {
  let config;
  try {
    config = useFunnelConfig();
  } catch (error) {
    console.error('Error getting funnel config:', error);
    return (
      <section className="relative bg-slate-900 text-white overflow-hidden min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl">Error loading funnel configuration</p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-slate-900 text-white overflow-hidden">
      {/* Dark professional background with subtle gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900/20 to-slate-900"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(30,58,138,0.3),transparent_50%)]"></div>
      
      <div className="container mx-auto px-4 py-4 md:py-16 lg:py-24 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Headline Callout */}
          <div className="mb-4 md:mb-12 text-center">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-2 md:mb-4">
              ONLY FOR Canadian Business Owners with companies worth <span className="text-amber-400">$5M+</span> & Incorporated Professionals earning <span className="text-amber-400">$500K+</span>
            </h1>
            <p className="text-base md:text-xl lg:text-2xl text-slate-200 mt-2 md:mt-4 max-w-4xl mx-auto">
              Click Through The Slides Below To See How Our Offer Works
            </p>
          </div>

          {/* Media Presentation - DSL or VSL */}
          <div className="mb-4 md:mb-12">
            <MediaPresentation
              config={config.media}
              onStartAssessment={onStartQuiz}
            />
          </div>

          {/* CTA Button or Quiz Questions */}
          <div className="mt-4 md:mt-12 text-center">
            {!quizStarted ? (
              <div className="space-y-2 md:space-y-4">
                <Button
                  onClick={onStartQuiz}
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 text-base md:text-lg lg:text-xl font-bold px-6 md:px-12 py-4 md:py-6 lg:py-8 rounded-lg shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 hover:scale-105"
                >
                  Schedule a Call
                  <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2" />
                </Button>
                <p className="text-xs md:text-sm text-slate-300 italic">
                  Want to See How This Plan Works? Our Advisor Will Walk You Through It in a 15-Minute Virtual Meeting
                </p>
              </div>
            ) : (
              <div id="inline-quiz" className="mt-8">
                <FunnelQuiz onComplete={onQuizComplete} inline={true} />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
